import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL = 2000;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;

    const poll = async (n) => {
      if (cancelled || n >= MAX_ATTEMPTS) {
        if (!cancelled) setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshMe();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), POLL_INTERVAL);
      } catch (e) {
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), POLL_INTERVAL);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, refreshMe]);

  return (
    <Layout title="Zahlung">
      <div className="max-w-xl mx-auto text-center py-12" data-testid="billing-success-page">
        {status === "polling" && (
          <>
            <Loader2 className="h-14 w-14 mx-auto text-emerald-500 animate-spin" />
            <h1 className="font-display text-2xl font-bold mt-5">Zahlung wird verarbeitet…</h1>
            <p className="text-zinc-500 mt-2">Versuch {attempts + 1} von {MAX_ATTEMPTS}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="font-display text-3xl font-bold mt-5">Zahlung erfolgreich!</h1>
            <p className="text-zinc-600 mt-3">Ihr Abo ist aktiv. Los geht's mit dem ersten Gutachten.</p>
            <div className="flex gap-3 mt-6 justify-center">
              <Button className="brand-gradient-bg text-white" onClick={() => navigate("/dashboard")} data-testid="billing-success-dashboard">
                Zum Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate("/billing")}>Abo-Details</Button>
            </div>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-amber-500" />
            <h1 className="font-display text-2xl font-bold mt-5">Verarbeitung dauert länger</h1>
            <p className="text-zinc-500 mt-2">Bitte später erneut prüfen. Sie erhalten eine E-Mail.</p>
            <Link to="/dashboard" className="mt-5 inline-block"><Button variant="outline">Zum Dashboard</Button></Link>
          </>
        )}
        {(status === "expired" || status === "error") && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-rose-500" />
            <h1 className="font-display text-2xl font-bold mt-5">Zahlung fehlgeschlagen</h1>
            <p className="text-zinc-500 mt-2">Bitte erneut versuchen.</p>
            <Link to="/pricing" className="mt-5 inline-block"><Button className="brand-gradient-bg text-white">Zurück zu Preisen</Button></Link>
          </>
        )}
      </div>
    </Layout>
  );
}
